import {CGP_EVENTS_NAMES, CGPEventSubscription, ICgpSdk} from './cgp-sdk.interface';
import { LiveTables } from '../live-table-feeds-service/live-table-feeds.service.interface';
import {TimeSpan} from '@sparkware/app-utils';
import {CLIENT_EVENTS, SYSTEM_EVENTS} from "../../../sdk/src/events/events";
import {
  IFreePlayVoucherData,
  IFreePlayVoucherDelta
} from "../../../common/interfaces/free-play/free-play-voucher-data.interface";
import {
  INetPositionGameTypeData
} from "../../../sdk/src/services/player_messages_service/firebase/handlers/netposition_handler.interfaces";
import {IJackpotDataById} from '../../../sdk/src/services/live_feeds/jackpots/jackpot_feeds_service_interface';
import {
  IGamblingSessionUpdateData
} from "../../../sdk/src/services/player_messages_service/firebase/handlers/gambling-session_handler_interfaces";
import {
  IGamblingSessionActionData, IOMGParsedData
} from "../../../sdk/src/services/player_messages_service/firebase/handlers/messages_handler_interfaces";

export interface OnShowGamePopupCallbackParams {
  gameType?: number,
  gamePopupApprovedCallback: () => void
}

export interface OnGameOpenParams {
  gameType: number;
  isReal: boolean;
}

export interface OnGameCloseParams {
  gameType: number;
  isReal: boolean;
  isGamePlayed: boolean;
  showNextGameSuggestion: boolean;
}

export interface OnPlayerActionParams {
  actionName: string;
  options?: any;
}

export interface OnOauthStateChangedParams {
  loginType?: number;
  logoutReason?: number;
  kickReason?: number;
  fromRegistration?: boolean;
}

export interface IGeoRestrictionsUpdateEventParams {
  KickReasonId?: string;
  TroubleshooterRetry?: boolean;
  DetailedMessages?: string[];
}

export interface IPeriodicGeoUpdateEventParams {
  mobileNumber: string;
  licenseKey: string;
  licenseWebKey: string;
  locationTransactionId: string;
}

export class SdkEvents {
  constructor(private CGP: ICgpSdk) {

  }

  onOAuthStateChanged(callback: (params: OnOauthStateChangedParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onBeforeOAuthStateChanged(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.ON_BEFORE_OAUTH_STATE_CHANGE,
      callback: callback,
      context: this
    });
  }

  onDeepLinkReceived(callback: (deepLinkObject: any) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.DEEPLINK_RECEIVED,
      callback: callback,
      context: this
    });
  }

  onShowGamePopup(callback: (args: OnShowGamePopupCallbackParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_POPUP_SHOW,
      callback: callback,
      context: this
    });
  }

  onGameOpen(callback: (params: OnGameOpenParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.GAME_OPEN,
      callback: callback,
      context: this
    });
  }

  onGameClose(callback: (params?: OnGameCloseParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.CLOSE_GAME,
      callback: callback,
      context: this
    });
  }


  onPlayerAction(callback: (eventData: OnPlayerActionParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.PLAYER_ACTION,
      callback: (data) => callback(data || {} as OnPlayerActionParams),
      context: this
    });
  }

  onRecentlyPlayedGamesUpdated(callback: (gameTypes: number[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.RECENTLY_PLAYED_GAMES_UPDATED,
      callback: callback,
      context: this
    });
  }

  onLoginFormOpen(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_OPEN,
      callback: callback,
      context: this
    });
  }

  onLoginFormClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LOGIN_FORM_CLOSE,
      callback: callback,
      context: this
    });
  }

  onTokenLifetimeExpired(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOKEN_LIFETIME_EXPIRED,
      callback: callback,
      context: this
    });
  }

  onLiveTableFeedsUpdate(callback: (tablesData: LiveTables) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_TABLES_UPDATE,
      callback: callback,
      context: this
    });
  }


  onJackpotsByIdUpdate(callback: (jackpots: unknown) => void) {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LIVE_FEEDS_JACKPOTS_BY_ID_UPDATED,
      callback,
      context: this
    });
  }

  onJackpotsAmountsByGameUpdate(callback: (jackpots: Record<string, number>) => void) {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LIVE_FEEDS_JACKPOT_AMOUNTS_BY_GAME_UPDATED,
      callback,
      context: this
    });
  }

  onJackpotsAmountsByIdUpdate(callback: (jackpots: Record<string, IJackpotDataById>) => void) {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LIVE_FEEDS_JACKPOTS_BY_ID_UPDATED,
      callback,
      context: this
    });
  }

  onBalanceUpdated(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.BALANCE_UPDATED,
      callback,
      context: this
    });
  }

  onSessionTimerUpdate(callback: (data: {sessionTime: TimeSpan}) => void) {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_TIMER_UPDATE,
      callback,
      context: this
    });
  }

  onRealityCheckOpened(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CLIENT_EVENTS.REALITY_CHECK_OPENED,
      callback,
      context: this
    });
  }

  onRealityCheckClosed(callback: () => void) {
    return this.CGP.onEvent({
      eventName: CLIENT_EVENTS.REALITY_CHECK_CLOSED,
      callback,
      context: this
    });
  }


  onRegistrationCompleted(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.REGISTRATION_COMPLETED,
      callback,
      context: this
    });
  }

  onAppResumed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_RESUMED,
      callback: callback,
      context: this
    });
  }

  onAppPaused(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.APP_PAUSED,
      callback: callback,
      context: this
    });
  }

  onExternalLinkClosed(callback: (linkSource: string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.EXTERNAL_LINK_CLOSED,
      callback: callback,
      context: this
    });
  }

  onTouchIDActiveStateChanged(callback: (state: boolean) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.TOUCH_ID_STATE_CHANGED,
      callback: callback,
      context: this
    });
  }

  onOmgDisplayed(callback: (state: unknown) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.OMG_DISPLAYED,
      callback,
      context: this
    });
  }

  onBurnoutMessageReceived(callback: (state: unknown) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.SESSION_EVENT_PLAYER_BURNOUT_MESSAGE_RECEIVED,
      callback,
      context: this
    });
  }

  onLiveGameSwitched(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.LIVE_GAME_SWITCHED,
      callback: callback,
      context: this
    });
  }

  onPromotionsChanged(callback: (data: {Cid: number, IsPromotionDisplayedInClient: boolean, IndicationInClientPromotionsIds: number[]}) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: CGP_EVENTS_NAMES.PROMOTIONS_CHANGED,
      callback: callback,
      context: this
    });
  }

  onDepositWidgetClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.DEPOSIT_WIDGET_CLOSED,
      callback: callback,
      context: this
    });
  }
  
  onFreeToPlayWidgetClosed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.FREE_TO_PLAY_WIDGET_CLOSED,
      callback: callback,
      context: this
    });
  }

  onPostMessage(callback: (message: MessageEvent) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.POST_MESSAGE_ARRIVE,
      callback: callback,
      context: this
    });
  }

  onHybridPostMessage(callback: (message: MessageEvent) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.HYBRID_POST_MESSAGE_ARRIVE,
      callback: callback,
      context: this
    });
  }

  onFreePlayBatchAdded(callback: (data: IFreePlayVoucherData[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_ADDED,
      callback: callback,
      context: this
    });
  }

  onFreePlayBatchUpdated(callback: (data: IFreePlayVoucherDelta[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_UPDATED,
      callback: callback,
      context: this
    });
  }

  onFreePlayBatchRemoved(callback: (voucherIds: string[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_BATCH_REMOVED,
      callback: callback,
      context: this
    });
  }


  onHybridEmbeddedGameLoadingIndicatorUpdateV1(callback: (percentage: number | string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOADING_INDICATOR_EMBEDDED_UPDATE,
      callback: callback,
      context: this
    });
  }

  onHybridEmbeddedGameLoadingIndicatorUpdateV2(callback: (eventData: {statusBar: number | string; numberOfPackages: string}) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOADING_INDICATOR_EMBEDDED_UPDATE_V2,
      callback: callback,
      context: this
    });
  }

  onFirebaseGotNewToken(callback: (eventData: {token: string}) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.FIREBASE_GOT_NEW_TOKEN,
      callback: callback,
      context: this
    });
  }

  onNetPositionUpdated(callback: (gamesTypesData: INetPositionGameTypeData[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_NET_POSITION_UPDATE,
      callback: callback,
      context: this
    });
  }

  onLoginSessionResumed(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.LOGIN_SESSION_RESUMED,
      callback: callback,
      context: this
    });
  }

  onUserApprovedPostLoginPopup(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.USER_APPROVE_GAME_POPUP,
      callback: callback,
      context: this
    });
  }

  onFirebaseOmgReceived(callback: (omgData: IOMGParsedData) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_OMG_RECEIVED,
      callback: callback,
      context: this
    });
  }

  onReceivedToaster(callback: (params: any[]) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SESSION_EVENT_RECEIVED_TOASTER,
      callback: callback,
      context: this
    });
  }

  onSecondWebViewClose(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.SECOND_WEB_VIEW_CLOSE,
      callback: callback,
      context: this
    });
  }

  onRestrictionsChanged(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.RESTRICTIONS_CHANGED,
      callback: callback,
      context: this
    });
  }

  onPlayerMessagesConnected(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY,
      callback: callback,
      context: this
    });
  }

  onGeoRestrictionsUpdate(callback: (params: IGeoRestrictionsUpdateEventParams) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GEO_RESTRICTIONS_UPDATE,
      callback: callback,
      context: this
    });
  }

  onGeoPeriodicUpdate(callback: (params: IPeriodicGeoUpdateEventParams) => void ): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GEO_PERIODIC_UPDATE,
      callback: callback,
      context: this
    });
  }

  onHybridGeoLocationResponse(callback: (result: string) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GEO_HYBRID_GET_LOCATION_RES,
      callback: callback,
      context: this
    });
  }

  onGamblingSessionUpdate(callback: (sessionData: IGamblingSessionUpdateData) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GAMBLING_SESSION_UPDATE,
      callback: callback,
      context: this
    });
  }

  onGamblingSessionAction(callback: (sessionData: IGamblingSessionActionData) => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.GAMBLING_SESSION_ACTION,
      callback: callback,
      context: this
    });
  }

  onServerTimeUpdated(callback: () => void): CGPEventSubscription {
    return this.CGP.onEvent({
      eventName: SYSTEM_EVENTS.TIME_UPDATED_FROM_SERVER,
      callback: callback,
      context: this
    });
  }


}
